<template>
  <aside class="sidebar" :class="{ collapsed: props.collapsed, open: props.open }">
    <div class="sidebar-content">
      <!-- Mobile header -->
      <div class="sidebar-mobile-header" v-if="props.open">
        <span class="sidebar-mobile-title">Menu</span>
        <button class="sidebar-close-btn" @click="emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Secoes dinamicas (accordion) -->
      <template v-for="secao in secoesVisiveis" :key="secao.id">
        <div class="menu-section">
          <div
            class="section-header"
            :class="{ 'section-open': secoesAbertas[secao.id] }"
            @click="toggleSecao(secao.id)"
          >
            <i :class="secao.icon"></i>
            <span>{{ secao.i18nKey ? t(secao.i18nKey) : secao.label }}</span>
            <i class="fas fa-chevron-down section-chevron" :class="{ rotated: secoesAbertas[secao.id] }"></i>
          </div>
          <ul class="sidebar-menu" v-show="secoesAbertas[secao.id]">
            <li v-for="item in secao.items" :key="item.path">
              <router-link
                :to="item.path"
                active-class="active"
                class="sidebar-link"
                @click="onLinkClick"
              >
                <i :class="item.icon"></i>
                <span>{{ item.i18nKey ? t(item.i18nKey) : item.nome }}</span>
              </router-link>
            </li>
          </ul>
        </div>
      </template>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/admin/authStore';
import { usePermissaoStore } from '@/stores/admin/permissaoStore';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  collapsed?: boolean
  open?: boolean
}>()

const emit = defineEmits(['close'])
const route = useRoute()

// Accordion — secoes abertas/fechadas
const secoesAbertas = reactive<Record<string, boolean>>({})

const toggleSecao = (id: string) => {
  secoesAbertas[id] = !secoesAbertas[id]
}

// Abrir automaticamente a secao que contem a rota ativa
const abrirSecaoAtiva = () => {
  const path = route.path
  for (const secao of secoesVisiveis.value) {
    if (secao.items.some(item => item.path === path)) {
      secoesAbertas[secao.id] = true
      return
    }
  }
}

const onLinkClick = () => {
  if (window.innerWidth < 640) {
    emit('close')
  }
}

const authStore = useAuthStore();
const permissaoStore = usePermissaoStore();

interface MenuItem {
  nome: string
  path: string
  icon: string
  secao: string
  modulo?: string
  i18nKey?: string
}

interface MenuSection {
  id: string
  label: string
  icon: string
  i18nKey?: string
  items: MenuItem[]
}

const secaoConfig: { id: string; label: string; icon: string; i18nKey?: string }[] = [
  { id: 'loja', label: 'Loja', icon: 'fas fa-store', i18nKey: 'nav.loja' },
  { id: 'estoque', label: 'Produtos & Estoque', icon: 'fas fa-boxes' },
  { id: 'financeiro', label: 'Financeiro', icon: 'fas fa-dollar-sign' },
  { id: 'gerencia', label: 'Gerencia', icon: 'fas fa-chart-line' },
  { id: 'servicos', label: 'Servicos', icon: 'fas fa-calendar-check' },
  { id: 'pet', label: 'Pet', icon: 'fas fa-paw' },
  { id: 'restaurante', label: 'Restaurante', icon: 'fas fa-utensils' },
  { id: 'veterinaria', label: 'Veterinaria', icon: 'fas fa-stethoscope' },
  { id: 'otica', label: 'Otica', icon: 'fas fa-glasses' },
  { id: 'admin', label: 'Administrador', icon: 'fas fa-shield-alt', i18nKey: 'nav.administracao' },
  { id: 'usuario', label: 'Minha Conta', icon: 'fas fa-user-cog' },
];

/**
 * Logica simplificada de visibilidade:
 * 1. Backend retorna TODAS as paginas ativas (tb_pagina) via permissaoStore
 * 2. modulosVisiveis (calculado por segmento+plano da loja) filtra quais aparecem
 * 3. Admin ve tudo — sem filtro
 * 4. Nao precisa mais de tb_perfil_permissao para controlar menu
 */
const resolvedItems = computed<MenuItem[]>(() => {
  const allPages = permissaoStore.menuItems || [];
  const modulosVisiveis = authStore.modulosVisiveis || [];

  // Converter para MenuItem, excluir blocos de dashboard
  const pages: MenuItem[] = allPages
    .filter(i => !i.path.startsWith('dashboard:'))
    .map(i => ({
      nome: i.nome,
      path: i.path,
      icon: i.icon,
      secao: i.secao || 'loja',
    }));

  // Admin ve tudo (mesma definicao de admin usada pelo guard de rota e pelo backend)
  if (authStore.isAdmin) {
    return pages;
  }

  // Filtrar por segmento+plano (modulosVisiveis)
  // A pagina aparece se:
  // - sua rota esta em modulosVisiveis, OU
  // - e uma pagina sem modulo guard (sempre visivel, como /home, /meu-perfil)
  if (modulosVisiveis.length > 0) {
    return pages.filter(item => modulosVisiveis.includes(item.path));
  }

  // Se modulosVisiveis estiver vazio (loja nao configurada), mostrar apenas comuns
  return pages.filter(item => ['/home', '/meu-perfil', '/lgpd'].includes(item.path));
});

const secoesVisiveis = computed<MenuSection[]>(() => {
  return secaoConfig
    .map(s => ({
      ...s,
      items: resolvedItems.value.filter(i => i.secao === s.id),
    }))
    .filter(s => s.items.length > 0);
});

const carregarPermissoes = async () => {
  const perfilIds = authStore.getPerfilIds;
  if (perfilIds && perfilIds.length) {
    await permissaoStore.carregarMenu(perfilIds);
  }
};

onMounted(() => {
  carregarPermissoes();
  setTimeout(abrirSecaoAtiva, 100)
});

watch(() => authStore.token, () => {
  carregarPermissoes();
});

watch(() => route.path, () => {
  abrirSecaoAtiva()
});
</script>
